import { BadRequestException, Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';

export interface CreatableStore {
  id: string;
  name: string;
  city: string | null;
  isActive: boolean;
}

/**
 * Roles that own the organization rather than a single shop within it.
 *
 * A merchant is the account holder: every store under their organization is
 * theirs, including ones created after any particular staff row. Everyone else
 * — a store manager, an accountant — holds an explicit assignment to specific
 * stores, and that assignment is the whole of their reach.
 */
const ORGANIZATION_ROLES = ['merchant'];

/**
 * Which stores a user may put a product into.
 *
 * Products are per-store rows (`Product.storeId`), so "add this to three
 * stores" is three products, not one shared record. That is the honest model:
 * after creation each one has its own stock, its own variants and its own
 * price, and a merchant who edits the Bangalore listing does not expect Chennai
 * to change underneath them.
 *
 * The list is computed server-side and every write is checked against it.
 * Sending a store id the caller cannot reach is refused rather than ignored —
 * silently dropping it would create fewer products than the merchant asked for
 * and tell them nothing.
 */
@Injectable()
export class ProductStoreScopeService {
  constructor(private readonly prisma: PrismaService) {}

  async creatableStores(userId: string): Promise<CreatableStore[]> {
    const user = await this.prisma.user.findUnique({
      where: { id: userId },
      select: { role: true, organizationId: true },
    });
    if (!user) return [];

    const select = { id: true, name: true, city: true, isActive: true };

    // The account holder: everything under the organization, including stores
    // created after this session began.
    if (ORGANIZATION_ROLES.includes(user.role) && user.organizationId) {
      return this.prisma.store.findMany({
        where: { organizationId: user.organizationId, deletedAt: null },
        select,
        orderBy: { name: 'asc' },
      });
    }

    /*
     * Everyone else is bounded by their staff assignments. Inactive
     * assignments do not count — a manager removed from a store should not be
     * able to keep filing products into it — which is the same rule
     * AdminAccessGuard applies to every other admin route.
     */
    const staff = await this.prisma.storeStaff.findMany({
      where: { userId, isActive: true, deletedAt: null },
      select: { storeId: true },
    });
    if (!staff.length) return [];

    return this.prisma.store.findMany({
      where: { id: { in: staff.map((s) => s.storeId) }, deletedAt: null },
      select,
      orderBy: { name: 'asc' },
    });
  }

  /**
   * Validate a requested set of stores, returning the ids to create in.
   *
   * @param requested the store ids from the request body; empty means "the
   *   store currently in context", which is how every existing caller behaves.
   * @param fallbackStoreId the context store, used when nothing was requested.
   */
  async resolveTargets(
    userId: string,
    requested: string[] | undefined,
    fallbackStoreId: string
  ): Promise<string[]> {
    if (!requested?.length) return [fallbackStoreId];

    const unique = [...new Set(requested)];
    const allowed = new Set((await this.creatableStores(userId)).map((s) => s.id));

    const refused = unique.filter((id) => !allowed.has(id));
    if (refused.length) {
      // Named rather than silently dropped: a merchant who asked for three
      // stores and got two would have no way to know which, or why.
      throw new BadRequestException(
        `You do not have access to ${refused.length === 1 ? 'this store' : 'these stores'}: ${refused.join(', ')}`
      );
    }

    return unique;
  }
}
